import axios from 'axios';
import React, { useState, useEffect } from 'react';
import './StyleCart.css';
import {
    Dropdown,
    DropdownMenu,
    DropdownToggle,
    Button,
    Badge,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    FormGroup,
    Label,
    Table,
    Row,
    Col
} from 'reactstrap';
import SimpleBar from "simplebar-react";
import { useAuth } from '../../Provider/AuthProvider';
import ClientSelectionModal from './ClientSelectionModal';
import { Cart, NewClient, CartItem } from "./Interfaces";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CartEditPage from './CartEditPage';

const MyCartDropdown = () => {
    const [isCartDropdown, setIsCartDropdown] = useState<boolean>(false);
    const [cart, setCart] = useState<Cart>({ client: '', items: [], clientInfo: null, order_id: null });
    const [total, setTotal] = useState<number>(0);
    const [clientModalOpen, setClientModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();
    const [largeEditModalOpen, setLargeEditModalOpen] = useState(false);
    const [editedItems, setEditedItems] = useState<CartItem[]>([]);

    const canIgnoreMinPrice = user.perms.produit.ignore_price_min_advance;
    const canEditPrice = user.perms.produit.edit_price_on_sale;
    const canApplyDiscount = canEditPrice && canIgnoreMinPrice;

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<CartItem | null>(null);
    const [editedQuantity, setEditedQuantity] = useState<number>(1);
    const [editedDiscount, setEditedDiscount] = useState<number>(0);

    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [createdOrderRef, setCreatedOrderRef] = useState<string>("");
    const [createdDevisRef, setCreatedDevisRef] = useState<string>("");
    const [createdPdfUrl, setCreatedPdfUrl] = useState<string>("");

    const [avoirModalOpen, setAvoirModalOpen] = useState(false);
    const [clientDiscounts, setClientDiscounts] = useState<any[]>([]);
    const [loadingDiscounts, setLoadingDiscounts] = useState(false);
    const [totalCreditNote, setTotalCreditNote] = useState<number>(0);
    const [selectedAvoirs, setSelectedAvoirs] = useState<number[]>([]);

    const [documentType, setDocumentType] = useState<'order' | 'proposal'>('order');
    const [isCreatingProposal, setIsCreatingProposal] = useState(false);

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [priceErrors, setPriceErrors] = useState<Record<string, string>>({});

    // États pour les commandes fournisseurs (Stock)
    const [supplierOrderRefs, setSupplierOrderRefs] = useState<string>("");
    const [isSupplierOrder, setIsSupplierOrder] = useState<boolean>(false);

    const isPriceEditingAllowed = (item: CartItem): boolean => {
        const minPrice = getMinPrice(item);
        const currentPrice = Number(item.price_ttc) || 0;
        if (minPrice === null || minPrice === undefined || minPrice === 0 || minPrice === currentPrice) {
            return false;
        }
        return canEditPrice;
    };

    const isDiscountEditingAllowed = (item: CartItem): boolean => {
        const minPrice = getMinPrice(item);
        if (minPrice === null || minPrice === undefined || minPrice === 0) {
            return true;
        }
        const currentPrice = Number(item.price_ttc) || 0;
        if (minPrice !== currentPrice) {
            return canApplyDiscount;
        }
        return false;
    };

    const getMinPrice = (item: CartItem): number | null => {
        if (!item.hasOwnProperty('price_min')) {
            return null;
        }
        if (item.price_min === null || item.price_min === undefined) {
            return null;
        }
        if (typeof item.price_min === 'string') {
            const parsed = parseFloat(item.price_min);
            return isNaN(parsed) ? null : parsed;
        }
        const priceMinNum = Number(item.price_min);
        return isNaN(priceMinNum) ? null : priceMinNum;
    };

    const calculatePriceAfterDiscount = (originalPrice: number, discountPercent: number): number => {
        if (!discountPercent || discountPercent <= 0) return originalPrice;
        const discountAmount = originalPrice * (discountPercent / 100);
        const finalPrice = originalPrice - discountAmount;
        return Math.round(finalPrice * 100) / 100;
    };

    const calculateItemTotal = (item: CartItem): number => {
        const price = Number(item.price_ttc) || 0;
        const discount = item.remise_percent || 0;
        if (discount > 0) {
            const discountedPrice = calculatePriceAfterDiscount(price, discount);
            return discountedPrice * item.quantity;
        }
        return price * item.quantity;
    };

    const getDecimalError = (item: CartItem): boolean => {
        if (typeof item.price_ttc === 'string') {
            const decimalPart = item.price_ttc.split(/[.,]/)[1];
            return decimalPart ? decimalPart.length > 2 : false;
        }
        return false;
    };

    const getQuantityError = (item: CartItem): boolean => {
        return item.quantity < 1;
    };

    const checkPriceValid = (item: CartItem, newPrice: number): string => {
        const minPrice = getMinPrice(item);
        if (newPrice <= 0) return 'Prix doit être > 0€';
        if (minPrice !== null && minPrice > 0) {
            const currentPrice = Number(item.price_ttc) || 0;
            if (minPrice !== currentPrice && newPrice < minPrice) {
                return 'Prix non autorisé';
            }
        }
        return '';
    };

    const canApplyDiscountToItem = (item: CartItem): boolean => {
        if (!isDiscountEditingAllowed(item)) return false;
        const minPrice = getMinPrice(item);
        const currentPrice = Number(item.price_ttc) || 0;
        return minPrice !== null && minPrice > 0 && currentPrice >= minPrice;
    };

    const getDiscountedPriceError = (item: CartItem, discountPercent: number = 0): boolean => {
        if (!discountPercent || discountPercent <= 0) return false;
        const minPrice = getMinPrice(item);
        if (minPrice === null || minPrice === 0) return false;
        const currentPrice = Number(item.price_ttc) || 0;
        if (minPrice === currentPrice) return true;
        const discountedPrice = calculatePriceAfterDiscount(currentPrice, discountPercent);
        return discountedPrice < minPrice;
    };

    const getDiscountError = (item: CartItem, discount: number): boolean => {
        if (discount < 0 || discount > 100) return true;
        const minPrice = getMinPrice(item);
        if (minPrice === null || minPrice === 0) {
            return discount > 20;
        }
        if (!isDiscountEditingAllowed(item)) {
            return true;
        }
        if (discount > 0 && getDiscountedPriceError(item, discount)) {
            return true;
        }
        return false;
    };

    useEffect(() => {
        const shouldOpenCart = localStorage.getItem('openCartOnVendeur');
        if (shouldOpenCart === 'true') {
            setIsCartDropdown(true);
            localStorage.removeItem('openCartOnVendeur');
            toast.success('Commande ajoutée au panier!');
        }
    }, []);

    const fetchClientDiscounts = async (clientRowid: string) => {
        if (!clientRowid) return;
        setLoadingDiscounts(true);
        try {
            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "getDiscounts",
                    socid: parseInt(clientRowid),
                    entity: user.entity
                },
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );
            if (response && response.success) {
                setClientDiscounts(response.discounts || []);
                setSelectedAvoirs([]);
            } else {
                setClientDiscounts([]);
                setTotalCreditNote(0);
            }
        } catch (error) {
            console.error("Error fetching discounts:", error);
            toast.error("Erreur lors de la récupération des avoirs");
            setClientDiscounts([]);
            setTotalCreditNote(0);
        } finally {
            setLoadingDiscounts(false);
        }
    };

    const openLargeEditModal = () => {
        resetValidationStates();
        const itemsWithDiscount = cart.items.map(item => ({
            ...item,
            remise_percent: item.remise_percent || 0
        }));
        setEditedItems([...itemsWithDiscount]);
        setLargeEditModalOpen(true);
    };

    const saveAllChanges = () => {
        const processedItems = editedItems.map(item => {
            let finalPrice = 0;
            if (typeof item.price_ttc === 'string') {
                const cleanedValue = item.price_ttc.replace(',', '.');
                finalPrice = parseFloat(cleanedValue) || 0;
                finalPrice = Math.round(finalPrice * 100) / 100;
            } else {
                finalPrice = Number(item.price_ttc) || 0;
            }
            return {
                ...item,
                price_ttc: finalPrice,
                remise_percent: item.remise_percent || 0,
                comment: item.comment || ''
            };
        });

        for (let item of processedItems) {
            const minPrice = getMinPrice(item);
            const unitPrice = Number(item.price_ttc) || 0;
            const discount = item.remise_percent || 0;
            const originalItem = cart.items.find(i => i.cartItemId === item.cartItemId);
            const originalPrice = originalItem ? Number(originalItem.price_ttc) || 0 : unitPrice;

            if (unitPrice <= 0) {
                toast.error(`Le prix de l'article ${item.articleName} doit être supérieur à 0€`);
                return;
            }
            if (!isPriceEditingAllowed(item)) {
                if (unitPrice !== originalPrice) {
                    toast.error(`Modification du prix non autorisée pour ${item.articleName}`);
                    return;
                }
            } else {
                if (minPrice !== null && minPrice > 0 && unitPrice < minPrice) {
                    toast.error(`Prix non autorisé pour ${item.articleName}`);
                    return;
                }
            }
            if (discount > 0) {
                if (!isDiscountEditingAllowed(item)) {
                    toast.error(`Remise non autorisée pour ${item.articleName}`);
                    return;
                }
                const discountedPrice = calculatePriceAfterDiscount(originalPrice, discount);
                if (minPrice !== null && minPrice > 0 && discountedPrice < minPrice) {
                    toast.error(`Prix non autorisé pour ${item.articleName}`);
                    return;
                }
            }
            if (item.quantity < 1) {
                toast.error(`La quantité pour l'article ${item.articleName} doit être au moins 1.`);
                return;
            }
        }

        const updatedCart = {
            ...cart,
            items: processedItems
        };
        localStorage.setItem('cart', JSON.stringify(updatedCart));
        window.dispatchEvent(new Event('cart-updated'));
        setLargeEditModalOpen(false);
        toast.success("Modifications enregistrées avec succès!");
    };

    const handleItemChange = (index: number, field: keyof CartItem, value: string) => {
        const updatedItems = [...editedItems];
        const currentItem = updatedItems[index];
        const minPrice = getMinPrice(currentItem);
        const currentPrice = Number(currentItem.price_ttc) || 0;

        if (field === 'remise_percent') {
            if (!isDiscountEditingAllowed(currentItem)) {
                toast.error('Remise non autorisée pour cet article');
                return;
            }
            const discountValue = value === '' ? 0 : parseFloat(value.replace(',', '.')) || 0;
            const validatedDiscount = Math.min(Math.max(discountValue, 0), 100);
            updatedItems[index] = {
                ...updatedItems[index],
                [field]: validatedDiscount
            };
            setEditedItems(updatedItems);
            updateCartTotal(updatedItems);
        } else if (field === 'price_ttc') {
            if (!isPriceEditingAllowed(currentItem)) {
                toast.error('Modification du prix non autorisée pour cet article');
                return;
            }
            const regex = /^(\d+)([.,](\d{0,2})?)?$/;
            if (value === '' || regex.test(value)) {
                const newPrice = value === '' ? 0 : parseFloat(value.replace(',', '.')) || 0;
                setPriceErrors(prev => ({ ...prev, [currentItem.cartItemId]: '' }));
                if (minPrice !== null && minPrice > 0 && newPrice < minPrice) {
                    setPriceErrors(prev => ({ ...prev, [currentItem.cartItemId]: 'Prix non autorisé' }));
                }
                updatedItems[index] = { ...currentItem, [field]: value };
                setEditedItems(updatedItems);
                updateCartTotal(updatedItems);
            }
        } else if (field === 'quantity') {
            const newValue = value === '' ? 0 : parseInt(value) || 0;
            updatedItems[index] = { ...currentItem, [field]: newValue };
            if (currentItem.parentCartItemId) {
                const parentIndex = updatedItems.findIndex(item => item.cartItemId === currentItem.parentCartItemId);
                if (parentIndex !== -1) {
                    updatedItems[parentIndex] = { ...updatedItems[parentIndex], quantity: newValue };
                }
            } else {
                const consigneIndex = updatedItems.findIndex(item => item.parentCartItemId === currentItem.cartItemId);
                if (consigneIndex !== -1) {
                    updatedItems[consigneIndex] = { ...updatedItems[consigneIndex], quantity: newValue };
                }
            }
            setEditedItems(updatedItems);
            updateCartTotal(updatedItems);
        } else if (field === 'comment') {
            updatedItems[index] = { ...currentItem, [field]: value };
            setEditedItems(updatedItems);
        } else {
            updatedItems[index] = { ...currentItem, [field]: value };
            setEditedItems(updatedItems);
        }
    };

    const updateCartTotal = (items: CartItem[]) => {
        const newTotal = items.reduce((sum, item) => {
            const price = typeof item.price_ttc === 'string'
                ? parseFloat(item.price_ttc.replace(',', '.')) || 0
                : Number(item.price_ttc) || 0;
            const discount = item.remise_percent || 0;
            let finalPrice = price;
            if (discount > 0) {
                finalPrice = calculatePriceAfterDiscount(price, discount);
            }
            return sum + (finalPrice * item.quantity);
        }, 0);
        setTotal(newTotal);
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
            const parsedCart = JSON.parse(savedCart);
            parsedCart.total = newTotal;
            localStorage.setItem('cart', JSON.stringify(parsedCart));
        }
    };

    const updateCartState = () => {
        const savedCart = localStorage.getItem('cart');
        const parsedCart: Cart = savedCart ? JSON.parse(savedCart) : {
            client: '',
            items: [],
            clientInfo: null
        };
        parsedCart.items = parsedCart.items.map(item => ({
            ...item,
            price_ttc: Number(item.price_ttc) || 0,
            remise_percent: item.remise_percent || 0
        }));
        setCart(parsedCart);
        const newTotal = parsedCart.items.reduce((sum, item) => {
            const price = Number(item.price_ttc) || 0;
            const discount = item.remise_percent || 0;
            let finalPrice = price;
            if (discount > 0) {
                finalPrice = calculatePriceAfterDiscount(price, discount);
            }
            return sum + (finalPrice * item.quantity);
        }, 0);
        setTotal(newTotal);
    };

    useEffect(() => {
        updateCartState();
        window.addEventListener('cart-updated', updateCartState);
        return () => window.removeEventListener('cart-updated', updateCartState);
    }, []);

    const toggleCartDropdown = () => setIsCartDropdown(!isCartDropdown);

    const removeItem = (cartItemId: string) => {
        const updatedCart: Cart = {
            ...cart,
            items: cart.items.filter(
                (item) => item.cartItemId !== cartItemId && item.parentCartItemId !== cartItemId
            )
        };
        setCart(updatedCart);
        localStorage.setItem('cart', JSON.stringify(updatedCart));
        window.dispatchEvent(new Event('cart-updated'));
    };

    const clearCartItems = () => {
        if (cart.items.length === 0) return;
        const updatedCart = {
            ...cart,
            items: [],
            order_id: null
        };
        setCart(updatedCart);
        localStorage.setItem('cart', JSON.stringify(updatedCart));
        window.dispatchEvent(new Event('cart-updated'));
        toast.success("Panier vidé (client conservé)");
    };

    // Modification ici : ne pas réinitialiser supplierOrderRefs et isSupplierOrder
    const clearCart = () => {
        localStorage.removeItem('cart');
        window.dispatchEvent(new Event('cart-updated'));
        setClientDiscounts([]);
        setTotalCreditNote(0);
        setSelectedAvoirs([]);
        setCart({ client: '', items: [], clientInfo: null, order_id: null });
        // On ne réinitialise PAS supplierOrderRefs et isSupplierOrder ici,
        // car ils sont utilisés pour l'affichage du modal de succès des commandes fournisseurs.
        // setSupplierOrderRefs("");
        // setIsSupplierOrder(false);
        toast.success("Panier et client supprimés");
    };

    const handleConfirmClient = (clientName: string, rowid?: string, newClientInfo?: NewClient) => {
        const updatedCart = {
            client: clientName,
            items: cart.items,
            clientInfo: rowid ? { rowid } : newClientInfo,
            order_id: cart.order_id,
            discounts: []
        };
        localStorage.setItem('cart', JSON.stringify(updatedCart));
        window.dispatchEvent(new Event('cart-updated'));
        setClientModalOpen(false);
        if (rowid) {
            fetchClientDiscounts(rowid);
        }
    };

    const calculateSelectedAvoirsTotal = () => {
        return clientDiscounts
            .filter(discount => selectedAvoirs.includes(discount.rowid))
            .reduce((total, discount) => total + parseFloat(discount.amount_ttc || "0"), 0);
    };

    const handleAvoirCheckboxChange = (avoirId: number) => {
        setSelectedAvoirs(prev => {
            if (prev.includes(avoirId)) {
                return prev.filter(id => id !== avoirId);
            } else {
                return [...prev, avoirId];
            }
        });
    };

    const openEditModal = (item: CartItem) => {
        resetValidationStates();
        setEditingItem({
            ...item,
            remise_percent: item.remise_percent || 0
        });
        setEditedQuantity(item.quantity);
        setEditedDiscount(item.remise_percent || 0);
        setEditModalOpen(true);
    };

    const saveEditedItem = () => {
        if (!editingItem) return;
        const unitPrice = Number(editingItem.price_ttc) || 0;
        const minPrice = getMinPrice(editingItem);
        const discount = editedDiscount;
        const originalItem = cart.items.find(i => i.cartItemId === editingItem.cartItemId);
        const originalPrice = originalItem ? Number(originalItem.price_ttc) || 0 : unitPrice;

        if (!isPriceEditingAllowed(editingItem)) {
            if (unitPrice !== originalPrice) {
                toast.error('Modification du prix non autorisée');
                return;
            }
        } else {
            if (unitPrice <= 0) {
                toast.error('Le prix doit être supérieur à 0€');
                return;
            }
            if (minPrice !== null && minPrice > 0 && unitPrice < minPrice) {
                toast.error('Prix non autorisé');
                return;
            }
        }
        if (discount > 0) {
            if (!isDiscountEditingAllowed(editingItem)) {
                toast.error('Remise non autorisée');
                return;
            }
            const discountedPrice = calculatePriceAfterDiscount(unitPrice, discount);
            if (minPrice !== null && minPrice > 0 && discountedPrice < minPrice) {
                toast.error('Prix non autorisé');
                return;
            }
        }
        if (editedQuantity < 1) {
            toast.error("La quantité doit être au moins 1.");
            return;
        }
        const updatedItems = cart.items.map(item => {
            if (item.cartItemId === editingItem.cartItemId) {
                return {
                    ...item,
                    quantity: editedQuantity,
                    price_ttc: unitPrice,
                    remise_percent: discount,
                    comment: editingItem.comment || ''
                };
            }
            if (item.parentCartItemId === editingItem.cartItemId) {
                return { ...item, quantity: editedQuantity };
            }
            if (editingItem.parentCartItemId && item.cartItemId === editingItem.parentCartItemId) {
                return { ...item, quantity: editedQuantity };
            }
            return item;
        });
        const updatedCart = { ...cart, items: updatedItems };
        localStorage.setItem('cart', JSON.stringify(updatedCart));
        window.dispatchEvent(new Event('cart-updated'));
        setEditModalOpen(false);
        toast.success("Article modifié avec succès!");
    };

    const handleSaveAllChanges = (updatedItems: CartItem[]) => {
        const updatedCart = { ...cart, items: updatedItems };
        localStorage.setItem('cart', JSON.stringify(updatedCart));
        window.dispatchEvent(new Event('cart-updated'));
    };

    // Fonction pour les commandes fournisseurs (client stock)
    const handleSupplierOrder = async () => {
       

        if (!cart.client || !cart.clientInfo) {
            
            setClientModalOpen(true);
            return;
        }

        if (cart.items.length === 0) {
            
            toast.error("Aucun article dans le panier");
            return;
        }

        for (const item of cart.items) {
            
            if (item.quantity < 1) {
                toast.error(`La quantité pour l'article "${item.articleName}" doit être au moins 1`);
                return;
            }
            if (!item.warehouse) {
                
                toast.error(`L'article "${item.articleName}" n'a pas d'entrepôt défini`);
                return;
            }
        }

        const userId = user?.id || user?.user_id || user?.userinfo?.id;
        

        if (!userId) {
            toast.error("Identifiant utilisateur manquant");
            return;
        }

        const lines = cart.items.map(item => ({
            product_id: Number(item.id),
            qty: Number(item.quantity),
            warehouse: String(item.warehouse)
        }));
        

        const orderData = {
            action: "createorder_fournisseur",
            user_id: parseInt(userId.toString(), 10),
            entity: user?.entity,
            lines: lines
        };

        

        try {
            setLoading(true);
            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                orderData,
                {
                    headers: {
                        Authorization: `Bearer ${user?.token}`,
                        "Content-Type": "application/json"
                    }
                }
            );
  
            

            // Supporte response.success ou response.data.success
            const isSuccess = response?.success === true || response?.data?.success === true;
            const supplierOrders = response?.supplier_orders || response?.data?.supplier_orders || [];

            if (isSuccess && supplierOrders.length > 0) {
                const refs = supplierOrders
                    .map((so: any) => `${so.ref} (${so.supplier_name})`)
                    .join(", ");
                
                setSupplierOrderRefs(refs);
                setIsSupplierOrder(true);
                setSuccessModalOpen(true);
                clearCart();  // maintenant clearCart ne réinitialise pas supplierOrderRefs/isSupplierOrder
            } else {
                const errorMessage = response?.message || response?.data?.message || "Erreur lors de la création de la commande fournisseur";
                console.error("Erreur API:", errorMessage);
                toast.error(errorMessage);
            }
        } catch (error: any) {
            console.error("Exception lors de l'appel API:", error);
            if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error("Erreur lors de la création de la commande fournisseur");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOrder = async () => {
        if (!cart.client || !cart.clientInfo) {
            setClientModalOpen(true);
            return;
        }

        if (cart.clientInfo.rowid === "9999999999") {
            await handleSupplierOrder();
            return;
        }

        for (const item of cart.items) {
            const unitPrice = Number(item.price_ttc) || 0;
            const minPrice = getMinPrice(item);
            const discount = item.remise_percent || 0;

            if (unitPrice <= 0) {
                toast.error(`Le prix de l'article "${item.articleName}" doit être supérieur à 0€`);
                return;
            }
            if (!isPriceEditingAllowed(item)) {
                const originalItem = cart.items.find(i => i.cartItemId === item.cartItemId);
                const originalPrice = originalItem ? Number(originalItem.price_ttc) || 0 : unitPrice;
                if (unitPrice !== originalPrice) {
                    toast.error(`Modification du prix non autorisée pour "${item.articleName}"`);
                    return;
                }
            } else {
                if (minPrice !== null && minPrice > 0 && unitPrice < minPrice) {
                    toast.error(`Prix non autorisé pour l'article "${item.articleName}"`);
                    return;
                }
            }
            if (discount > 0) {
                if (!isDiscountEditingAllowed(item)) {
                    toast.error(`Remise non autorisée pour "${item.articleName}"`);
                    return;
                }
                const discountedPrice = calculatePriceAfterDiscount(unitPrice, discount);
                if (minPrice !== null && minPrice > 0 && discountedPrice < minPrice) {
                    toast.error(`Prix non autorisé pour l'article "${item.articleName}"`);
                    return;
                }
            }
            if (item.quantity < 1) {
                toast.error(`La quantité pour l'article "${item.articleName}" doit être au moins 1`);
                return;
            }
        }

        const orderData = {
            action: "createorder",
            entity: user?.entity || '',
            socid: cart?.clientInfo.rowid,
            order_id: cart?.order_id,
            lines: cart.items.map(item => ({
                id: item.lineId || '',
                product_id: item.id,
                unitprice: Number(item.price_ttc) || 0,
                qty: item.quantity,
                immat: item.immat || "",
                warehouse: item.warehouse,
                parent_id: item.parentCartItemId
                    ? cart.items.find(i => i.cartItemId === item.parentCartItemId)?.id
                    : "",
                remise_percent: item.remise_percent || 0,
                comment: item.comment || ""
            })),
            company: cart?.clientInfo
        };

        try {
            setLoading(true);
            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                orderData,
                {
                    headers: {
                        Authorization: `Bearer ${user?.token}`,
                        "Content-Type": "application/json"
                    }
                }
            );
            if (response && response.success !== false) {
                const orderRef = response.order_ref || "Inconnue";
                setCreatedOrderRef(orderRef);
                setCreatedPdfUrl(response.resultPDF || "");
                setIsSupplierOrder(false);
                setSuccessModalOpen(true);
                clearCart();
            } else {
                const errorMessage = response?.message || "Erreur lors de la création de la commande";
                toast.error(errorMessage);
                return;
            }
        } catch (error: any) {
            console.error("Error creating order:", error);
            if (error.response) {
                const serverError = error.response.data;
                if (serverError && serverError.message) {
                    toast.error(serverError.message);
                } else if (serverError && typeof serverError === 'string') {
                    toast.error(serverError);
                } else {
                    toast.error("Erreur serveur lors de la création de la commande");
                }
            } else if (error.request) {
                toast.error("Aucune réponse du serveur. Vérifiez votre connexion.");
            } else {
                toast.error("Erreur lors de la création de la commande");
            }
            return;
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProposal = async () => {
        if (!cart.client || !cart.clientInfo) {
            setClientModalOpen(true);
            return;
        }

        for (const item of cart.items) {
            const unitPrice = Number(item.price_ttc) || 0;
            const minPrice = getMinPrice(item);
            const discount = item.remise_percent || 0;

            if (unitPrice <= 0) {
                toast.error(`Le prix de l'article "${item.articleName}" doit être supérieur à 0€`);
                return;
            }
            if (!isPriceEditingAllowed(item)) {
                const originalItem = cart.items.find(i => i.cartItemId === item.cartItemId);
                const originalPrice = originalItem ? Number(originalItem.price_ttc) || 0 : unitPrice;
                if (unitPrice !== originalPrice) {
                    toast.error(`Modification du prix non autorisée pour "${item.articleName}"`);
                    return;
                }
            } else {
                if (minPrice !== null && minPrice > 0 && unitPrice < minPrice) {
                    toast.error(`Prix non autorisé pour l'article "${item.articleName}"`);
                    return;
                }
            }
            if (discount > 0) {
                if (!isDiscountEditingAllowed(item)) {
                    toast.error(`Remise non autorisée pour "${item.articleName}"`);
                    return;
                }
                const discountedPrice = calculatePriceAfterDiscount(unitPrice, discount);
                if (minPrice !== null && minPrice > 0 && discountedPrice < minPrice) {
                    toast.error(`Prix non autorisé pour l'article "${item.articleName}"`);
                    return;
                }
            }
            if (item.quantity < 1) {
                toast.error(`La quantité pour l'article "${item.articleName}" doit être au moins 1`);
                return;
            }
        }

        const proposalData = {
            action: "createpropal",
            entity: user?.entity || '',
            socid: cart?.clientInfo.rowid,
            lines: cart.items.map(item => ({
                id: item.lineId || '',
                product_id: item.id,
                unitprice: Number(item.price_ttc) || 0,
                qty: item.quantity,
                immat: item.immat || "",
                warehouse: item.warehouse,
                parent_id: item.parentCartItemId
                    ? cart.items.find(i => i.cartItemId === item.parentCartItemId)?.id
                    : "",
                remise_percent: item.remise_percent || 0,
            })),
            company: cart?.clientInfo
        };

        try {
            setIsCreatingProposal(true);
            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                proposalData,
                {
                    headers: {
                        Authorization: `Bearer ${user?.token}`,
                        "Content-Type": "application/json"
                    }
                }
            );
            if (response && response.success !== false) {
                const proposalRef = response.propal_ref || "Inconnue";
                setCreatedOrderRef(proposalRef);
                setCreatedPdfUrl(response.resultPDF || "");
                setSuccessModalOpen(true);
                clearCart();
            } else {
                const errorMessage = response?.message || "Erreur lors de la création du devis";
                toast.error(errorMessage);
            }
        } catch (error: any) {
            console.error("Error creating proposal:", error);
            if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error("Erreur lors de la création du devis");
            }
        } finally {
            setIsCreatingProposal(false);
        }
    };

    const resetValidationStates = () => {
        setPriceErrors({});
        setValidationErrors({});
    };

    return (
        <>
            <Dropdown isOpen={isCartDropdown} toggle={toggleCartDropdown} className="topbar-head-dropdown ms-1 header-item">
                <DropdownToggle tag="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle">
                    <i className='bx bx-shopping-bag fs-22'></i>
                    <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-info">
                        {cart.items.length}
                    </span>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-xl dropdown-menu-end p-0 dropdown-menu-cart shadow-xl">
                    <SimpleBar style={{ maxHeight: "300px" }}>
                        <div className="p-2">
                            {cart.client && (
                                <div className="p-3 border-top-0 border-start-0 border-end-0 border-dashed border">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <i
                                                className="ri-delete-bin-line text-danger cursor-pointer"
                                                style={{ fontSize: '18px' }}
                                                onClick={() => { clearCart(); }}
                                                title="Supprimer tout (client + panier)"
                                            ></i>
                                            <h6 className="m-0 fs-16 fw-semibold">{cart.client}</h6>
                                            {clientDiscounts.length > 0 && (
                                                <Badge
                                                    color="warning"
                                                    className="cursor-pointer ms-2 p-2"
                                                    onClick={() => setAvoirModalOpen(true)}
                                                    style={{ cursor: 'pointer', color: "black", fontSize: '10px' }}
                                                    title="Voir les avoirs"
                                                >
                                                    <i className="ri-coupon-line me-1"></i>
                                                    Avoir ({clientDiscounts.length})
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    {totalCreditNote > 0 && (
                                        <div className="mt-1">
                                            <small className="text-success">
                                                <i className="ri-wallet-3-line me-1"></i>
                                                Crédit disponible: {totalCreditNote.toFixed(2)} €
                                            </small>
                                        </div>
                                    )}
                                </div>
                            )}
                            {cart.items.length === 0 ? (
                                <div className="text-center empty-cart">
                                    <div className="avatar-md mx-auto my-3">
                                        <div className="avatar-title bg-info-subtle text-info fs-36 rounded-circle">
                                            <i className='bx bx-cart'></i>
                                        </div>
                                    </div>
                                    <h5 className="mb-3">Le panier est vide!</h5>
                                    {!cart.client && (
                                        <>
                                            <div className="text-center mb-2">
                                                <Button
                                                    color="primary"
                                                    size="m"
                                                    outline
                                                    onClick={() => setClientModalOpen(true)}
                                                >
                                                    <i className="ri-user-add-line me-1"></i>
                                                    Ajouter un client
                                                </Button>
                                                <p className="text-muted mt-1 mb-0 small">Aucun client sélectionné</p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : (
                                cart.items.map((item) => {
                                    const hasDiscount = item.remise_percent && item.remise_percent > 0;
                                    const originalPrice = Number(item.price_ttc) || 0;
                                    const discountedPrice = hasDiscount
                                        ? calculatePriceAfterDiscount(originalPrice, item.remise_percent || 0)
                                        : originalPrice;
                                    const itemTotal = discountedPrice * item.quantity;

                                    return (
                                        <div key={item.cartItemId} className="d-block dropdown-item text-wrap dropdown-item-cart px-3 py-2">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                    <h6 className="mt-0 mb-1 fs-14">{item.articleName || 'Produit inconnu'}</h6>
                                                    <Badge className="me-1" color="primary">{item.brandName}</Badge>
                                                    <p className="mb-0 fs-12 text-muted">
                                                        Référence: {item.ref || 'N/A'}<br />
                                                        Quantité: {item.quantity} × {discountedPrice.toFixed(2)}€
                                                    </p>
                                                </div>
                                                <div className="px-2 text-end">
                                                    {hasDiscount ? (
                                                        <div className="d-flex flex-column align-items-end">
                                                            <Badge
                                                                color="transparent"
                                                                className="mb-1"
                                                                style={{
                                                                    border: '2px solid #198754',
                                                                    backgroundColor: 'transparent',
                                                                    color: '#198754',
                                                                    fontWeight: '600',
                                                                    fontSize: '11px',
                                                                    padding: '3px 8px',
                                                                    borderRadius: '12px'
                                                                }}
                                                            >
                                                                <i className="ri-percent-line me-1"></i>
                                                                -{item.remise_percent}%
                                                            </Badge>
                                                            <small className="text-decoration-line-through text-muted">
                                                                {(originalPrice * item.quantity).toFixed(2)}€
                                                            </small>
                                                            <h5 className="m-0 text-success fw-bold">
                                                                {itemTotal.toFixed(2)}€
                                                            </h5>
                                                        </div>
                                                    ) : (
                                                        <h5 className="m-0">
                                                            {itemTotal.toFixed(2)}€
                                                        </h5>
                                                    )}
                                                </div>
                                                <div className="ps-2 d-flex flex-column">
                                                    <button
                                                        type="button"
                                                        className="btn btn-icon btn-sm btn-ghost-secondary"
                                                        onClick={() => removeItem(item.cartItemId)}
                                                        title="Supprimer"
                                                    >
                                                        <i className="ri-delete-bin-line text-danger cursor-pointer fs-20"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </SimpleBar>
                    {cart.items.length > 0 && (
                        <div className="p-3 border-bottom-0 border-start-0 border-end-0 border-dashed border">
                            <div className="d-flex justify-content-between align-items-center pb-3">
                                <h5 className="m-0 text-muted">Total:</h5>
                                <div className="px-2">
                                    <h5 className="m-0">{total.toFixed(2)}€</h5>
                                </div>
                            </div>
                            <Button
                                color="danger"
                                onClick={clearCartItems}
                                className="w-100 mb-2"
                                title="Vider seulement les articles"
                            >
                                <i className="ri-shopping-cart-2-line me-1"></i>
                                Vider panier
                            </Button>
                            <Button
                                color="primary"
                                className="w-100 mb-2"
                                onClick={openLargeEditModal}
                            >
                                <i className="ri-edit-line me-2"></i>
                                Modifier le panier
                            </Button>
                            <Row>
                                <Col md="6">
                                    <Button
                                        color="warning"
                                        className="w-100 mb-2"
                                        onClick={() => {
                                            setDocumentType('proposal');
                                            handleCreateProposal();
                                        }}
                                        disabled={isCreatingProposal || !cart.client}
                                    >
                                        {isCreatingProposal ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Création...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-file-paper-line me-2"></i>
                                                Créer Devis
                                            </>
                                        )}
                                    </Button>
                                </Col>
                                {user?.perms?.commande?.creer === 1 && (
                                    <Col md="6">
                                        <Button
                                            color="success"
                                            className="w-100 mb-2"
                                            onClick={() => {
                                                setDocumentType('order');
                                                handleOrder();
                                            }}
                                            disabled={loading || !cart.client}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner size="sm" className="" />
                                                    En cours...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-shopping-cart-line me-1"></i>
                                                    Commander
                                                </>
                                            )}
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    )}
                </DropdownMenu>
            </Dropdown>

            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />

            <ClientSelectionModal
                isOpen={clientModalOpen}
                toggle={() => setClientModalOpen(false)}
                onConfirm={(clientName, rowid, newClientInfo) => {
                    handleConfirmClient(clientName, rowid, newClientInfo);
                }}
            />

            <Modal isOpen={avoirModalOpen} toggle={() => setAvoirModalOpen(false)} size="lg">
                <ModalHeader toggle={() => {
                    setAvoirModalOpen(false);
                    setSelectedAvoirs([]);
                }}>
                    <div className="d-flex align-items-center">
                        <i className="ri-coupon-line me-2 fs-4 text-warning"></i>
                        <span>Avoirs disponibles - {cart.client}</span>
                        {selectedAvoirs.length > 0 && (
                            <Badge color="primary" className="ms-2">
                                {selectedAvoirs.length} sélectionné{selectedAvoirs.length > 1 ? 's' : ''}
                            </Badge>
                        )}
                    </div>
                </ModalHeader>
                <ModalBody>
                    {loadingDiscounts ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                            <p className="mt-2">Chargement des avoirs...</p>
                        </div>
                    ) : clientDiscounts.length > 0 ? (
                        <>
                            <div className="alert alert-info mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <i className="ri-information-line me-2"></i>
                                        Total crédit disponible
                                    </div>
                                    <strong className="fs-5">
                                        {clientDiscounts
                                            .reduce((sum, discount) => sum + parseFloat(discount.amount_ttc || "0"), 0)
                                            .toFixed(2)} €
                                    </strong>
                                </div>
                            </div>
                            <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                <Table hover bordered>
                                    <thead>
                                        <tr>
                                            <th>Référence</th>
                                            <th className="text-end">Montant TTC</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clientDiscounts.map((avoir, index) => (
                                            <tr key={index} className={avoir.used ? 'table-warning' : ''}>
                                                <td>
                                                    <div className="d-flex flex-column">
                                                        <span className="fw-semibold">{avoir.ref}</span>
                                                        {avoir.used && (
                                                            <small className="text-warning">
                                                                <i className="ri-checkbox-circle-line me-1"></i>
                                                                Déjà utilisé
                                                            </small>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-end fw-bold text-success">
                                                    {Number(avoir.amount_ttc).toFixed(2)} €
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-4">
                            <i className="ri-coupon-line display-1 text-muted mb-3"></i>
                            <h5 className="text-muted">Aucun avoir disponible</h5>
                            <p className="text-muted">Ce client n'a pas d'avoir à utiliser.</p>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setAvoirModalOpen(false)}>
                        Fermer
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={successModalOpen} toggle={() => setSuccessModalOpen(false)} centered>
                <ModalHeader toggle={() => setSuccessModalOpen(false)}>
                    <i className="ri-checkbox-circle-line text-success me-2"></i>
                    {isSupplierOrder
                        ? "Commandes fournisseurs créées"
                        : (documentType === 'proposal' ? 'Devis créé avec succès' : 'Commande créée avec succès')}
                </ModalHeader>
                <ModalBody className="text-center py-4">
                    <div className="mb-3">
                        <i className="ri-check-double-line display-1 text-success"></i>
                    </div>
                    {isSupplierOrder ? (
                        <>
                            <h4>Commandes fournisseurs enregistrées</h4>
                            <p className="text-muted mt-3">Références des commandes :</p>
                            <div className="bg-light p-3 rounded">
                                <strong className="fs-18 text-primary">{supplierOrderRefs}</strong>
                            </div>
                            <p className="text-muted mt-2 small">Chaque commande a été créée chez son fournisseur respectif.</p>
                        </>
                    ) : (
                        <>
                            <h4>
                                {documentType === 'proposal'
                                    ? 'Votre devis a été enregistré'
                                    : 'Votre commande a été enregistrée'}
                            </h4>
                            <p className="text-muted mt-3">
                                {documentType === 'proposal' ? 'Référence du devis :' : 'Référence de la commande :'}
                            </p>
                            <div className="bg-light p-3 rounded">
                                <strong className="fs-18 text-primary">{createdOrderRef}</strong>
                            </div>
                        </>
                    )}
                </ModalBody>
                <ModalFooter className="justify-content-center">
                    {!isSupplierOrder && createdPdfUrl && (
                        <Button
                            color="success"
                            onClick={() => window.open(createdPdfUrl, '_blank')}
                            className="me-2"
                        >
                            <i className="ri-download-2-line me-1"></i>
                            Télécharger PDF
                        </Button>
                    )}
                    <Button color="primary" onClick={() => setSuccessModalOpen(false)}>
                        Fermer
                    </Button>
                </ModalFooter>
            </Modal>

            <CartEditPage
                isOpen={largeEditModalOpen}
                toggle={() => {
                    setLargeEditModalOpen(false);
                    resetValidationStates();
                }}
                cartItems={cart.items}
                clientName={cart.client}
                onSaveAllChanges={handleSaveAllChanges}
                getMinPrice={getMinPrice}
                isPriceEditingAllowed={isPriceEditingAllowed}
                isDiscountEditingAllowed={isDiscountEditingAllowed}
                calculatePriceAfterDiscount={calculatePriceAfterDiscount}
                getDiscountError={getDiscountError}
                getDecimalError={getDecimalError}
                getQuantityError={getQuantityError}
            />

            <Modal
                isOpen={editModalOpen}
                toggle={() => {
                    setEditModalOpen(false);
                    resetValidationStates();
                }}
            >
                <ModalHeader toggle={() => setEditModalOpen(false)}>Modifier l'article</ModalHeader>
                <ModalBody>
                    {editingItem && (
                        <>
                            <FormGroup>
                                <Label>Article</Label>
                                <Input
                                    type="text"
                                    value={editingItem.articleName}
                                    disabled
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Référence</Label>
                                <Input
                                    type="text"
                                    value={editingItem.ref}
                                    disabled
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Remise %</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={editedDiscount === 0 ? '' : editedDiscount}
                                    onChange={(e) => {
                                        if (!isDiscountEditingAllowed(editingItem)) {
                                            toast.error('Remise non autorisée pour cet article');
                                            return;
                                        }
                                        const value = e.target.value;
                                        const discount = value === '' ? 0 : parseFloat(value) || 0;
                                        const validatedDiscount = Math.min(Math.max(discount, 0), 100);
                                        setEditedDiscount(validatedDiscount);
                                    }}
                                    className={getDiscountError(editingItem, editedDiscount) ? 'is-invalid border-danger' : ''}
                                    placeholder="0"
                                    readOnly={!isDiscountEditingAllowed(editingItem)}
                                    style={!isDiscountEditingAllowed(editingItem) ? {
                                        backgroundColor: '#f8f9fa',
                                        cursor: 'not-allowed',
                                        color: '#6c757d'
                                    } : getDiscountError(editingItem, editedDiscount) ? {
                                        borderColor: '#dc3545',
                                        borderWidth: '2px'
                                    } : {}}
                                    title={!isDiscountEditingAllowed(editingItem) ? "Remise non autorisée pour cet article" :
                                        getDiscountError(editingItem, editedDiscount) ? "Valeur incorrecte" : ""}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Prix unitaire (TTC)</Label>
                                <Input
                                    type="text"
                                    value={typeof editingItem.price_ttc === 'string' ? editingItem.price_ttc : (editingItem.price_ttc?.toFixed(2) || '')}
                                    onChange={(e) => {
                                        if (!isPriceEditingAllowed(editingItem)) {
                                            toast.error('Modification du prix non autorisée pour cet article');
                                            return;
                                        }
                                        const regex = /^(\d+)([.,](\d{0,2})?)?$/;
                                        if (e.target.value === '' || regex.test(e.target.value)) {
                                            setEditingItem({
                                                ...editingItem,
                                                price_ttc: e.target.value
                                            });
                                        }
                                    }}
                                    className={priceErrors[editingItem?.cartItemId] ? 'is-invalid border-danger' : ''}
                                    placeholder="0.00"
                                    readOnly={!isPriceEditingAllowed(editingItem)}
                                    style={!isPriceEditingAllowed(editingItem) ? {
                                        backgroundColor: '#f8f9fa',
                                        cursor: 'not-allowed',
                                        color: '#6c757d'
                                    } : priceErrors[editingItem?.cartItemId] ? {
                                        borderColor: '#dc3545',
                                        borderWidth: '2px'
                                    } : {}}
                                    title={!isPriceEditingAllowed(editingItem) ? "Prix fixe - modification non autorisée" :
                                        priceErrors[editingItem?.cartItemId] ? "Prix non autorisé" : ""}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Quantité</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={editedQuantity}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const newQuantity = val === '' ? 0 : parseInt(val) || 0;
                                        setEditedQuantity(newQuantity);
                                    }}
                                    className={validationErrors.quantity ? 'border-danger' : ''}
                                    style={validationErrors.quantity ? {
                                        borderColor: '#dc3545',
                                        borderWidth: '2px'
                                    } : {}}
                                    title={validationErrors.quantity ? "Quantité doit être ≥ 1" : ""}
                                />
                            </FormGroup>
                            <div className="text-end mt-2">
                                {editedDiscount > 0 ? (
                                    <div>
                                        <strong className="text-success">Total après remise: </strong>
                                        <span className="text-success fw-bold">
                                            {calculatePriceAfterDiscount(Number(editingItem.price_ttc) || 0, editedDiscount).toFixed(2)}€ × {editedQuantity} =
                                            {(calculatePriceAfterDiscount(Number(editingItem.price_ttc) || 0, editedDiscount) * editedQuantity).toFixed(2)}€
                                        </span>
                                    </div>
                                ) : (
                                    <div>
                                        <strong>Total: </strong>
                                        {((Number(editingItem.price_ttc) || 0) * editedQuantity).toFixed(2)}€
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setEditModalOpen(false)}>
                        Annuler
                    </Button>
                    <Button
                        color="primary"
                        onClick={saveEditedItem}
                    >
                        Enregistrer
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default MyCartDropdown;